import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import {
  Code,
  Volume2,
  ArrowRight,
  ShieldCheck,
  Database,
  Wifi,
  Coffee,
  Lightbulb,
  Users,
  FileCheck,
  Plane,
  Briefcase,
  Globe
} from 'lucide-react';

const TECH_TERMS = [
  { de: 'die Datenbank', en: 'database' },
  { de: 'die Schnittstelle', en: 'API / interface' },
  { de: 'die Bereitstellung', en: 'deployment' },
  { de: 'die Störung', en: 'outage / disruption' },
  { de: 'der Server', en: 'server' },
  { de: 'das WLAN', en: 'Wi-Fi' },
  { de: 'die Internetverbindung', en: 'internet connection' },
  { de: 'die künstliche Intelligenz', en: 'artificial intelligence' },
  { de: 'die KI', en: 'AI (short form)' },
  { de: 'der Code', en: 'code' },
  { de: 'die Cloud', en: 'cloud' },
  { de: 'die Verschlüsselung', en: 'encryption' },
];

const COMMS = [
  { de: 'Wo ist die Schnittstelle dokumentiert?', en: 'Where is the API documented?' },
  { de: 'Was ist das WLAN-Passwort?', en: "What's the Wi-Fi password?" },
  { de: 'Können wir das Deployment heute machen?', en: 'Can we deploy today?' },
  { de: 'Der Server ist down.', en: 'The server is down.' },
];

const ITCareerPage = () => {
  const [playingAudio, setPlayingAudio] = useState(null);

  const speak = (text, id) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = 'de-DE';
      u.rate = 0.9;
      u.onstart = () => setPlayingAudio(id);
      u.onend = () => setPlayingAudio(null);
      window.speechSynthesis.speak(u);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white" data-testid="it-career-page">
      <Navbar />

      {/* Hero */}
      <section className="py-16 md:py-20 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100/40 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="container mx-auto max-w-4xl relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Code className="w-4 h-4" />
            Career Path
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-babyblue-800 mb-4">
            IT &amp; Technology
          </h1>
          <p className="text-xl text-babyblue-600 leading-relaxed max-w-3xl mx-auto">
            Informationstechnologie — the most English-friendly path into the German job market.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-8">
            <p className="text-babyblue-700 text-lg leading-relaxed mb-4">
              If you want to work in IT, you're lucky — most companies do <strong>not</strong> require German.
              Many international IT professionals work in Germany using English as the day-to-day workplace language.
            </p>
            <p className="text-babyblue-700 text-lg leading-relaxed">
              Still, learning some German vocab will help you join the small talk at lunch, in the kitchen, and during coffee hopping — and it earns you a lot of goodwill.
            </p>
          </div>
        </div>
      </section>

      {/* Ideal things to dive into */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-babyblue-800 mb-2 text-center">
            Ideal Things to Dive Into
          </h2>
          <p className="text-babyblue-600 text-center mb-8">German-specific regulations that shape every IT project here.</p>

          <div className="space-y-5">
            {/* DSGVO */}
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6" data-testid="it-dsgvo-card">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-xl flex-shrink-0">
                  <ShieldCheck className="w-7 h-7 text-blue-700" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-babyblue-800 mb-1">
                    Datenschutz-Grundverordnung (DSGVO)
                  </h3>
                  <p className="text-babyblue-500 text-sm italic mb-3">a.k.a. GDPR — General Data Protection Regulation</p>
                  <p className="text-babyblue-700 leading-relaxed">
                    Get familiar with the DSGVO. It defines how personal data is stored, processed, transferred, and how user privacy is enforced online in Germany and the entire EU. Every product decision, every database design, every analytics integration has to comply.
                  </p>
                </div>
              </div>
            </div>

            {/* BSI */}
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6" data-testid="it-bsi-card">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-xl flex-shrink-0">
                  <ShieldCheck className="w-7 h-7 text-blue-700" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-babyblue-800 mb-1">
                    Bundesamt für Sicherheit in der Informationstechnik (BSI)
                  </h3>
                  <p className="text-babyblue-500 text-sm italic mb-3">Federal Office for Information Security</p>
                  <p className="text-babyblue-700 leading-relaxed">
                    The BSI defines Germany's framework for internet and IT security. If you work with the <strong>financial sector</strong>, the <strong>automotive industry</strong>, or the <strong>public sector</strong>, your code and infrastructure must follow BSI guidelines (e.g., IT-Grundschutz).
                  </p>
                </div>
              </div>
            </div>

            {/* Betriebsrat */}
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6" data-testid="it-betriebsrat-card">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-emerald-100 rounded-xl flex-shrink-0">
                  <Users className="w-7 h-7 text-emerald-700" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-babyblue-800 mb-1">Der Betriebsrat</h3>
                  <p className="text-babyblue-500 text-sm italic mb-3">Works council</p>
                  <p className="text-babyblue-700 leading-relaxed">
                    Many German companies have a Betriebsrat — the elected body that represents employee rights.
                    Anything that touches <strong>employee performance tracking</strong>, <strong>working hours</strong>, or <strong>behavioural monitoring</strong> (think: time-tracking tools, analytics on devs, AI-based productivity software) has to be <strong>approved by the Betriebsrat</strong> before you can roll it out.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tech vocab */}
      <section className="py-12 px-6 bg-white">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-heading text-3xl font-bold text-babyblue-800 mb-2 text-center">
            Technical Vocabulary
          </h2>
          <p className="text-babyblue-600 text-center mb-8">Click any word to hear it.</p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="it-vocab-grid">
            {TECH_TERMS.map((word, i) => (
              <button
                key={i}
                onClick={() => speak(word.de, `tech-${i}`)}
                className="bg-babyblue-50 rounded-xl p-4 border border-babyblue-200 hover:border-primary hover:shadow-md transition-all text-left group flex items-start justify-between gap-3"
                data-testid={`it-vocab-${i}`}
              >
                <div>
                  <p className="font-bold text-babyblue-800">{word.de}</p>
                  <p className="text-sm text-babyblue-600">{word.en}</p>
                </div>
                <div className={`p-2 rounded-full flex-shrink-0 transition-all ${
                  playingAudio === `tech-${i}` ? 'bg-primary text-white' : 'bg-white text-primary opacity-0 group-hover:opacity-100'
                }`}>
                  <Volume2 className="w-4 h-4" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Communication */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-babyblue-800 mb-2 text-center">
            Lunch-break Phrases
          </h2>
          <p className="text-babyblue-600 text-center mb-8 flex items-center justify-center gap-2">
            <Coffee className="w-4 h-4" />
            For coffee hopping &amp; daily standups
          </p>

          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6">
            <div className="space-y-2">
              {COMMS.map((phrase, i) => (
                <button
                  key={i}
                  onClick={() => speak(phrase.de, `comm-${i}`)}
                  className="w-full text-left bg-babyblue-50 hover:bg-babyblue-100 p-4 rounded-lg border border-babyblue-200 transition-all flex items-center justify-between"
                  data-testid={`it-comm-${i}`}
                >
                  <div>
                    <p className="font-semibold text-babyblue-800">{phrase.de}</p>
                    <p className="text-sm text-babyblue-600 italic">{phrase.en}</p>
                  </div>
                  <Volume2 className={`w-4 h-4 flex-shrink-0 ${playingAudio === `comm-${i}` ? 'text-primary animate-pulse' : 'text-babyblue-400'}`} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Visa & Recognition */}
      <section className="py-12 px-6 bg-gradient-to-b from-babyblue-50 to-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-babyblue-800 mb-2 text-center">
            Working in Germany: Visa &amp; Recognition
          </h2>
          <p className="text-babyblue-600 text-center mb-8">IT is one of Germany's official shortage occupations (<em>Engpassberuf</em>) — visas are faster here.</p>

          <div className="space-y-4">
            {/* Step 1: Language */}
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <Globe className="w-5 h-5 text-blue-600" />
                    <h3 className="font-heading text-lg font-bold text-babyblue-800">Language Requirement</h3>
                    <span className="text-babyblue-500 text-sm">(Sprachniveau)</span>
                  </div>
                  <p className="text-babyblue-700 mb-3">Most IT employers accept <strong>English-only</strong> work. German is a bonus, not a barrier.</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-white px-3 py-1 rounded-full text-sm border border-blue-200">
                      <strong className="text-blue-700">English B2</strong> — minimum for daily standups
                    </span>
                    <span className="bg-white px-3 py-1 rounded-full text-sm border border-blue-200">
                      <strong className="text-blue-700">German A2+</strong> — nice-to-have for integration
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2: Recognition */}
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">2</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <FileCheck className="w-5 h-5 text-blue-600" />
                    <h3 className="font-heading text-lg font-bold text-babyblue-800">Diploma Recognition</h3>
                    <span className="text-babyblue-500 text-sm">(Anerkennung)</span>
                  </div>
                  <p className="text-babyblue-700 mb-3">
                    IT is largely unregulated in Germany — you usually don't need formal recognition. Employers care about your portfolio &amp; experience.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                      <p className="text-green-800 text-sm"><strong>EU/EEA:</strong> No recognition required — work freely.</p>
                    </div>
                    <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                      <p className="text-amber-800 text-sm"><strong>Non-EU:</strong> Bachelor's degree (any field) + job offer is enough for an EU Blue Card.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3: Visa */}
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <Plane className="w-5 h-5 text-blue-600" />
                    <h3 className="font-heading text-lg font-bold text-babyblue-800">Work Visa</h3>
                    <span className="text-babyblue-500 text-sm">(Arbeitsvisum / Blaue Karte EU)</span>
                  </div>
                  <p className="text-babyblue-700">
                    IT is an <strong>Engpassberuf</strong> (shortage occupation) — the EU Blue Card salary threshold is lowered to <strong>€45,934.20/year (2025)</strong>. The Chancenkarte (Opportunity Card) is also a great option if you don't have a job offer yet.{' '}
                    <Link to="/integration/visa" className="text-primary font-semibold underline">Read the full visa guide →</Link>
                  </p>
                </div>
              </div>
            </div>

            {/* Step 4: Start Working */}
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">4</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <Briefcase className="w-5 h-5 text-blue-600" />
                    <h3 className="font-heading text-lg font-bold text-babyblue-800">Start Working</h3>
                  </div>
                  <p className="text-babyblue-700">
                    Join a German tech team — IT salaries in Berlin, Munich, Hamburg, and Frankfurt regularly hit €70k–€110k for experienced developers, security engineers, and ML/AI specialists.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6 bg-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-xl font-bold text-babyblue-800 mb-6">Explore Other Career Paths</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { icon: '🏢', title: 'Business & Office', path: '/careers/business' },
              { icon: '🔧', title: 'Engineering', path: '/careers/engineering' },
              { icon: '🏥', title: 'Healthcare', path: '/careers/healthcare' },
            ].map((item, i) => (
              <Link
                key={i}
                to={item.path}
                className="bg-babyblue-50 rounded-xl border border-babyblue-200 p-4 flex items-center gap-3 hover:border-primary hover:shadow-md transition-all"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="text-babyblue-700 font-medium">{item.title}</span>
                <ArrowRight className="w-4 h-4 text-babyblue-400 ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ITCareerPage;
